import { Money } from '../../../components/ui/Money.js';
import { TableSkeleton } from '../../../components/ui/Skeleton.js';
import { EmptyState } from '../../../components/ui/EmptyState.js';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../lib/locale.js';
import { useSubscriberDividends, type DividendCreditItem } from '../network/payouts.api.js';

interface DividendCreditsTableProps {
  subscriberId: string;
}

export function DividendCreditsTable({ subscriberId }: DividendCreditsTableProps) {
  const { data, isLoading, error } = useSubscriberDividends(subscriberId);
  const credits: DividendCreditItem[] = Array.isArray(data) ? data : (data?.data ?? []);

  if (isLoading) return <TableSkeleton columns={4} />;

  if (error) {
    return (
      <p className="text-sm text-danger-text p-3 bg-danger-bg rounded-lg">
        Failed to load dividend credits.
      </p>
    );
  }

  if (credits.length === 0) {
    return (
      <EmptyState
        title="No dividend credits"
        description="Dividend credits appear after each auction cycle."
        variant="no-data"
      />
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-surface-2 border-b border-border">
            <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary uppercase tracking-wide">Cycle</th>
            <th className="px-3 py-2 text-right text-xs font-medium text-text-secondary uppercase tracking-wide">Dividend</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary uppercase tracking-wide">Applied to Due</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-text-secondary uppercase tracking-wide">Date</th>
          </tr>
        </thead>
        <tbody>
          {credits.map((c) => (
            <tr key={c.id} className="border-b border-border last:border-0 hover:bg-surface-2 transition-colors">
              <td className="px-3 py-2.5 font-mono text-xs text-text-secondary">{c.cycleId.slice(0, 8)}…</td>
              <td className="px-3 py-2.5 text-right tnum">
                <Money paise={c.dividendAmount} currency={DEFAULT_CURRENCY} locale={DEFAULT_LOCALE} emphasis="table" />
              </td>
              <td className="px-3 py-2.5 font-mono text-xs text-text-secondary">
                {c.appliedToDueId ? `${c.appliedToDueId.slice(0, 8)}…` : <span className="text-text-muted">—</span>}
              </td>
              <td className="px-3 py-2.5 text-xs text-text-secondary">
                {new Date(c.createdAt).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
